import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useSarLabAuth } from './SarLabAuthContext';
import { chatApi, rainApi, ActiveRain } from '../services/api';

interface Message {
  id: string;
  text: string;
  userId: string;
  username: string;
  timestamp: number;
  type: 'text' | 'system';
  isAdmin?: boolean;
  isDeleted?: boolean;
}

interface OnlineUser {
  odvisitId: string;
  odvisitUsername: string;
  lastActivity: string;
}

interface RainWinner {
  odvisitId: string;
  odvisitUsername: string;
  amount: number;
}

interface ChatContextType {
  messages: Message[];
  onlineUsers: OnlineUser[];
  isConnected: boolean;
  unreadCount: number;
  sendMessage: (text: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  typingUsers: string[];
  markAsRead: () => void;
  // Ban/Timeout
  isBanned: boolean;
  isTimedOut: boolean;
  timeoutUntil: number | null;
  // Rain
  activeRain: ActiveRain | null;
  rainCountdown: number;
  isParticipatingInRain: boolean;
  rainWinners: RainWinner[];
  showRainNotification: boolean;
  dismissRainNotification: () => void;
}

const ChatContext = createContext<ChatContextType>({} as ChatContextType);

export const useChat = () => {
  return useContext(ChatContext);
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { currentPlayer } = useSarLabAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [typingUsers] = useState<string[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Ban/Timeout state
  const [isBanned, setIsBanned] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timeoutUntil, setTimeoutUntil] = useState<number | null>(null);

  // Rain state
  const [activeRain, setActiveRain] = useState<ActiveRain | null>(null);
  const [rainCountdown, setRainCountdown] = useState(0);
  const [isParticipatingInRain, setIsParticipatingInRain] = useState(false);
  const [rainWinners, setRainWinners] = useState<RainWinner[]>([]);
  const [showRainNotification, setShowRainNotification] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  // Mesajları ve rain durumunu getir
  const fetchChatData = useCallback(async () => {
    if (!currentPlayer) return;

    try {
      // Chat mesajlarını getir
      const chatResponse = await chatApi.getMessages();
      if (chatResponse.success && chatResponse.data?.messages) {
        const formattedMessages: Message[] = chatResponse.data.messages.map((msg) => ({
          id: msg.id,
          text: msg.text,
          userId: msg.userId,
          username: msg.username,
          timestamp: msg.timestamp,
          type: 'text' as const
        }));

        // Yeni mesaj varsa unread count artır
        if (formattedMessages.length > lastMessageCount && lastMessageCount > 0) {
          setUnreadCount(prev => prev + (formattedMessages.length - lastMessageCount));
        }
        setLastMessageCount(formattedMessages.length);
        setMessages(formattedMessages);

        // Ban/Timeout durumunu güncelle
        const restrictions = chatResponse.data.restrictions;
        if (restrictions) {
          setIsBanned(restrictions.isBanned);
          if (restrictions.timeoutUntil) {
            const timeoutTime = new Date(restrictions.timeoutUntil).getTime();
            if (timeoutTime > Date.now()) {
              setIsTimedOut(true);
              setTimeoutUntil(timeoutTime);
            } else {
              setIsTimedOut(false);
              setTimeoutUntil(null);
            }
          } else {
            setIsTimedOut(false);
            setTimeoutUntil(null);
          }
        } else {
          setIsBanned(false);
          setIsTimedOut(false);
          setTimeoutUntil(null);
        }
      }

      // Aktif rain kontrolü
      const rainResponse = await rainApi.getActiveRain();
      if (rainResponse.success && rainResponse.data?.activeRain) {
        const rain = rainResponse.data.activeRain;
        setActiveRain(rain);

        // Rain countdown hesapla
        const startedAt = new Date(rain.startedAt).getTime();
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, 20 - Math.floor(elapsed / 1000));
        setRainCountdown(remaining);

        // Yeni rain başladıysa notification göster
        if (!activeRain || activeRain.id !== rain.id) {
          setShowRainNotification(true);
          setIsParticipatingInRain(false);
        }
      } else if (activeRain && activeRain.status === 'active') {
        // Rain bitti
        setActiveRain(null);
        setRainCountdown(0);
      }
    } catch (error) {
      console.error('Chat data fetch error:', error);
    }
  }, [currentPlayer, lastMessageCount, activeRain]);

  // Polling - 3 saniyede bir
  useEffect(() => {
    if (!currentPlayer) return;

    fetchChatData();
    const interval = setInterval(fetchChatData, 3000);

    return () => clearInterval(interval);
  }, [currentPlayer, fetchChatData]);

  // Rain countdown timer
  useEffect(() => {
    if (rainCountdown <= 0 || !activeRain) return;

    const timer = setInterval(() => {
      setRainCountdown(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rainCountdown, activeRain]);

  // Mesaj gönder
  const sendMessage = async (text: string) => {
    if (!currentPlayer || !text.trim()) {
      return;
    }

    try {
      const response = await chatApi.sendMessage(text.trim());

      if (response.success) {
        // Mesaj gönderildi, rain'e otomatik katıl
        if (activeRain && activeRain.status === 'active' && rainCountdown > 0 && !isParticipatingInRain) {
          try {
            const participateResponse = await rainApi.participate(activeRain.id);
            if (participateResponse.success) {
              setIsParticipatingInRain(true);
            }
          } catch (error) {
            console.error('Rain participation error:', error);
          }
        }

        // Hemen yenile
        await fetchChatData();
      }
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  };

  // Yazıyor göstergesi
  const setTyping = (_isTyping: boolean) => {
    // WebSocket ile implemente edilecek
  };

  // Mesaj silme (admin only - frontend'de kullanılmayacak)
  const deleteMessage = async (_messageId: string) => {
    // Admin panel tarafından yapılacak
  };

  // Mesajları okundu olarak işaretle
  const markAsRead = () => {
    setUnreadCount(0);
  };

  // Rain notification'ı kapat
  const dismissRainNotification = () => {
    setShowRainNotification(false);
  };

  // Suppress unused variable warnings
  void setOnlineUsers;
  void setRainWinners;

  const value = {
    messages,
    onlineUsers,
    isConnected: !!currentPlayer,
    unreadCount,
    sendMessage,
    deleteMessage,
    setTyping,
    typingUsers,
    markAsRead,
    // Restrictions
    isBanned,
    isTimedOut,
    timeoutUntil,
    // Rain
    activeRain,
    rainCountdown,
    isParticipatingInRain,
    rainWinners,
    showRainNotification,
    dismissRainNotification
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
